import { useAuthContext } from '@/hooks/use-auth-context'
import {
  CurrentUserDTO,
  OrganizationTypes,
  PermissionKey
} from '@/api/generated/model'
import { SuperAdminRoleID } from '@/constants/role-ids'

/** The permission keys the signed-in user's role holds; none when signed out. */
export const permissionKeysOf = (
  account: CurrentUserDTO | undefined
): PermissionKey[] => account?.Permissions ?? []

/** Whether the signed-in user's role holds the permission key, whatever the organization. */
export const useHasPermission = (permission: PermissionKey): boolean => {
  const { account } = useAuthContext()
  return permissionKeysOf(account).includes(permission)
}

/**
 * Whether the signed-in person is a Super Admin. Work only a Super Admin may do has no
 * permission key, so it is gated by the role. Viewing as another organization does not change it.
 */
export const useIsSuperAdmin = (): boolean => {
  const { account } = useAuthContext()
  return account?.Role?.ID === SuperAdminRoleID
}

/** Gates the Users page: the API's admin user search wants user.read. */
export const useCanViewUsers = (): boolean => useHasPermission('user.read')

/** Gates price sheet edits (region): the API's price sheet update wants price-sheet.edit. */
export const useCanManagePriceSheet = (): boolean =>
  useHasPermission('price-sheet.edit')

/** Gates price sheet deletion: the API allows only the Super Admin role. */
export const useCanDeletePriceSheet = (): boolean => useIsSuperAdmin()

/**
 * Gates the agreement Files tab (source files, list and download): the API wants
 * agreement-file.read, which the Admin and Super Admin roles hold.
 */
export const useCanReadAgreementFiles = (): boolean =>
  useHasPermission('agreement-file.read')

/** Gates product alias review: the API wants product-alias.review, which Admins hold. */
export const useCanReviewProductAliases = (): boolean =>
  useHasPermission('product-alias.review')

/**
 * Mirrors the API's claim-manage authorization for gating UI affordances: the caller must hold
 * claim.edit and belong to the organization that owns the action. Super admins satisfy the
 * membership half, matching `IUserContext.BelongsToOrganization` server-side.
 *
 * This reads the permission keys on the current user (`GET /idm/users`), not the legacy
 * partnership-scoped grants from the old permissions service. Those two disagree: a user can hold one
 * and not the other, so gating on the legacy grant either hides an action the API would allow or shows
 * one it now refuses with a 403.
 */
export const useCanManageForOrganization = (
  organizationID: string | undefined
): boolean =>
  useHasRolePermissionForOrganizations('claim.edit', [organizationID], true)

/**
 * True when the caller holds a role permission and belongs to one of the organizations on the
 * record. Super admins satisfy the membership half, matching `IUserContext.BelongsToOrganization`
 * server-side.
 *
 * The role model has one role per user, so it has no per-partnership grants. A record names its
 * manufacturer and its distributor, and the caller is on one side or the other.
 */
const useHasRolePermissionForOrganizations = (
  permission: PermissionKey,
  organizationIDs: Array<string | undefined>,
  superAdminBypassesMembership: boolean
): boolean => {
  const { account } = useAuthContext()

  if (!account) {
    return false
  }

  if (!permissionKeysOf(account).includes(permission)) {
    return false
  }

  if (superAdminBypassesMembership && account.Role?.ID === SuperAdminRoleID) {
    return true
  }

  return organizationIDs.some(
    (organizationID) =>
      organizationID !== undefined && organizationID === account.OrganizationID
  )
}

/**
 * Gates claim writes either side of the claim may make, such as uploading an attachment:
 * claim.edit plus membership in the claim's manufacturer or distributor; super admins pass membership.
 */
export const useCanManageClaim = (
  manufacturerID: string | undefined,
  distributorID: string | undefined
): boolean =>
  useHasRolePermissionForOrganizations(
    'claim.edit',
    [manufacturerID, distributorID],
    true
  )

/**
 * Gates the claim file downloads: a read, so it wants claim.read.
 *
 * No super-admin bypass here, unlike the manage gate. The download endpoints compare the caller's
 * organization to the claim's two directly, so a super admin outside both gets a 404 — showing the
 * control would only offer a download that fails.
 */
export const useCanReadClaim = (
  manufacturerID: string | undefined,
  distributorID: string | undefined
): boolean =>
  useHasRolePermissionForOrganizations(
    'claim.read',
    [manufacturerID, distributorID],
    false
  )

/**
 * Gates the transaction writes, which require transaction.edit on the API: edit, validate,
 * match quote numbers, archive and restore.
 */
export const useCanManageTransactions = (
  distributorID: string | undefined
): boolean =>
  useHasRolePermissionForOrganizations(
    'transaction.edit',
    [distributorID],
    true
  )

/**
 * Gates the agreement writes both sides may make (rename, flags, region, items, branches,
 * customers, categories, validate, publish): the API wants agreement.edit and membership in the
 * agreement's manufacturer or distributor organization; super admins pass membership.
 */
export const useCanManageAgreement = (
  manufacturerID: string | undefined,
  distributorID: string | undefined
): boolean =>
  useHasRolePermissionForOrganizations(
    'agreement.edit',
    [manufacturerID, distributorID],
    true
  )

/**
 * Gates agreement and agreement-file deletion: the API wants agreement.delete and membership in
 * the agreement's manufacturer organization, or a super admin.
 */
export const useCanDeleteAgreement = (
  manufacturerID: string | undefined
): boolean =>
  useHasRolePermissionForOrganizations(
    'agreement.delete',
    [manufacturerID],
    true
  )

/**
 * Mirrors the API's price sync settings write gate
 * (PriceSyncController.EnsureCallerCanManageExportSettings): price-sync.edit plus membership in
 * the distributor organization; super admins pass membership. It covers formats, saved
 * configurations and schedules. Running an export or a schedule now is NOT gated by this — the API
 * allows it with price-sync.export.
 */
export const useCanManageExportSchedules = (
  distributorID: string | undefined
): boolean =>
  useHasRolePermissionForOrganizations('price-sync.edit', [distributorID], true)

/**
 * The permission each upload document type needs on the API (POST /data-transformation/upload).
 * A price sheet upload needs price-sheet.upload, not price-sheet.edit: editing stays Admin-only
 * for the price sheet admin page, while Users upload price sheets from the user menu.
 */
const UploadDocumentTypePermissions: Record<string, PermissionKey> = {
  SPA: 'agreement.edit',
  Pricesheet: 'price-sheet.upload',
  POSTransactions: 'transaction.edit',
  ClaimResponse: 'claim.edit'
}

/**
 * The document types the caller may upload from the user menu, mirroring the API: a manufacturer
 * or distributor organization, the permission each type needs, and claim responses only from a
 * manufacturer. Empty when the caller may upload nothing.
 */
export const useUploadableDocumentTypes = (): string[] => {
  const { account, organization } = useAuthContext()
  const organizationType = organization?.OrganizationType
  const isManufacturer = organizationType === OrganizationTypes.Manufacturer

  if (
    !account ||
    (!isManufacturer && organizationType !== OrganizationTypes.Distributor)
  ) {
    return []
  }

  const held = new Set<PermissionKey>(permissionKeysOf(account))

  return Object.entries(UploadDocumentTypePermissions)
    .filter(
      ([documentType, permission]) =>
        held.has(permission) &&
        (documentType !== 'ClaimResponse' || isManufacturer)
    )
    .map(([documentType]) => documentType)
}
